// Leitura da planilha de conferência da Gran (#6116) — o formato "ASSOCIADOS GRAN - GUILHERME".
// A planilha da Gran é a lista oficial: quem está nela entra ou é atualizado, quem não está
// aparece na prévia para a pessoa decidir se sai (começa desmarcado). Nada aqui grava: só monta o plano que a tela mostra
// antes de confirmar. As colunas são achadas pelo título, não pela posição — a Gran mexe na
// ordem das colunas de um mês para o outro.
import type { ConferenciaGran, ItemPlanilhaGran, UcPlanilhaGran } from './planilhaGranModel.ts';

export interface LinhaConferenciaLida {
  /** Linha no Excel (1 = cabeçalho), para a pessoa achar na planilha dela. */
  linha: number;
  numeros: string[];
  nome: string;
  anotacao: string | null;
  conferencia: ConferenciaGran;
}

export interface LeituraConferencia {
  linhas: LinhaConferenciaLida[];
  /** Colunas obrigatórias que não foram achadas (a leitura não serve sem elas). */
  faltando: string[];
}

const semAcento = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/\s+/g, ' ').trim();
const digitos = (s: unknown) => String(s ?? '').replace(/\D/g, '').replace(/^0+/, '');
const texto = (v: unknown) => { const t = String(v ?? '').trim(); return t || null; };

/** Título da coluna → como reconhecer. A primeira que casar vence. */
const COLUNAS = {
  ucNova: [/^uc nova$/],
  ucAntiga: [/^uc antiga$/],
  ligacao: [/^ligacao$/],
  nome: [/^nome do associado$/, /^nome$/],
  anotacao: [/^coluna ?4$/, /^anotac/, /^observac/],
  anotacao2: [/^coluna ?2$/],
  desconto: [/^desconto hfp/, /^desconto$/],
  noRateio: [/^esta no rateio/],
  vaiTrocar: [/^vai trocar para vigor/],
  titularDoc: [/^documento$/],
  clienteDoc: [/^cpf\/cnpj$/],
} satisfies Record<string, RegExp[]>;

function simNao(v: unknown): boolean | null {
  const t = semAcento(String(v ?? ''));
  return t === 'sim' ? true : t === 'nao' ? false : null;
}

/** "0,35", "35%", 0.35 ou 35 → 0.35. Vazio ou texto → null. */
function lerDesconto(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null;
  const s = String(v).trim().replace('%', '').replace(',', '.');
  const n = Number(s);
  if (!Number.isFinite(n)) return null;
  return n > 1 || String(v).includes('%') ? n / 100 : n;
}

/**
 * Lê a matriz da aba (primeira linha com "UC Nova" ou "UC Antiga" é o cabeçalho).
 * `fonte` e `hoje` vão para a conferência de cada linha.
 */
export function lerPlanilhaConferencia(matriz: unknown[][], fonte: string, hoje: string): LeituraConferencia {
  const idxCab = matriz.findIndex(r => (r || []).some(c => /^uc (nova|antiga)$/.test(semAcento(String(c ?? '')))));
  if (idxCab < 0) return { linhas: [], faltando: ['UC Nova ou UC Antiga'] };
  const cab = (matriz[idxCab] || []).map(c => semAcento(String(c ?? '')));
  const col = Object.fromEntries(Object.entries(COLUNAS).map(([k, pads]) => {
    for (const p of pads) { const i = cab.findIndex(c => p.test(c)); if (i >= 0) return [k, i]; }
    return [k, -1];
  })) as Record<keyof typeof COLUNAS, number>;
  const faltando = col.ucNova < 0 && col.ucAntiga < 0 && col.ligacao < 0 ? ['UC Nova ou UC Antiga'] : [];
  const cel = (r: unknown[], k: keyof typeof COLUNAS) => (col[k] >= 0 ? r[col[k]] : null);

  const linhas: LinhaConferenciaLida[] = [];
  matriz.slice(idxCab + 1).forEach((r, i) => {
    const row = r || [];
    // UC Nova primeiro; a antiga é o plano B (o cadastro pode ainda ter só o número antigo).
    const numeros = [cel(row, 'ucNova'), cel(row, 'ligacao'), cel(row, 'ucAntiga')].map(digitos).filter(Boolean);
    if (!numeros.length) return;
    const conf: ConferenciaGran = { fonte, conferido_em: hoje };
    const anot1 = texto(cel(row, 'anotacao'));
    let desconto = lerDesconto(cel(row, 'desconto'));
    // A coluna de desconto guarda o valor de antes; quando a anotação manda "alterar desconto
    // para X%", o conferido é o X (Julianna/Valéria em 28/09: coluna 100%, anotação 35%).
    const alvo = anot1?.match(/alterar desconto para\s*(\d+(?:[.,]\d+)?)\s*%/i);
    if (alvo) desconto = Number(alvo[1].replace(',', '.')) / 100;
    if (desconto !== null) conf.desconto = desconto;
    const rateio = simNao(cel(row, 'noRateio'));
    if (rateio !== null) conf.no_rateio = rateio;
    const troca = simNao(cel(row, 'vaiTrocar'));
    if (troca !== null) conf.vai_trocar_para_vigor = troca;
    const tit = texto(cel(row, 'titularDoc'));
    if (tit) conf.titular_documento = tit;
    const cli = texto(cel(row, 'clienteDoc'));
    if (cli) conf.cliente_documento = cli;
    const notas = [anot1, texto(cel(row, 'anotacao2'))].filter(Boolean);
    linhas.push({
      linha: idxCab + i + 2,
      numeros: Array.from(new Set(numeros)),
      nome: texto(cel(row, 'nome')) || '',
      anotacao: notas.length ? notas.join(' | ') : null,
      conferencia: conf,
    });
  });
  return { linhas, faltando };
}

export interface PlanoImportacao {
  atualizar: { itemId: number; numero: string; nome: string; anotacao: string | null; conferencia: ConferenciaGran }[];
  incluir: { ucId: number; numero: string; nome: string; anotacao: string | null; conferencia: ConferenciaGran }[];
  sair: { itemId: number; numero: string; grupo: string }[];
  naoAchadas: { linha: number; numero: string; nome: string }[];
}

/**
 * Compara a planilha lida com a lista atual. A UC é achada pelo número (sem máscara nem zero
 * à esquerda) em `numero_uc` ou `outro_numero_uc`. Linha repetida da mesma UC vale uma vez só.
 */
export function planejarImportacao(linhas: LinhaConferenciaLida[], itens: ItemPlanilhaGran[], ucs: UcPlanilhaGran[]): PlanoImportacao {
  const porNumero = new Map<string, UcPlanilhaGran>();
  ucs.forEach(u => [u.numero_uc, u.outro_numero_uc].forEach(n => {
    const d = digitos(n);
    if (d && !porNumero.has(d)) porNumero.set(d, u);
  }));
  const itemPorUc = new Map(itens.map(i => [Number(i.unidade_consumidora_id), i]));
  const plano: PlanoImportacao = { atualizar: [], incluir: [], sair: [], naoAchadas: [] };
  const vistos = new Set<number>();
  for (const l of linhas) {
    const uc = l.numeros.map(n => porNumero.get(n)).find(Boolean);
    if (!uc) { plano.naoAchadas.push({ linha: l.linha, numero: l.numeros[0], nome: l.nome }); continue; }
    const id = Number(uc.id);
    if (vistos.has(id)) continue;
    vistos.add(id);
    const base = { numero: uc.numero_uc || l.numeros[0], nome: uc.nome_cliente || l.nome, anotacao: l.anotacao, conferencia: l.conferencia };
    const item = itemPorUc.get(id);
    if (item) plano.atualizar.push({ itemId: item.id, ...base });
    else plano.incluir.push({ ucId: id, ...base });
  }
  itens.forEach(i => {
    if (!vistos.has(Number(i.unidade_consumidora_id))) plano.sair.push({ itemId: i.id, numero: i.numero_uc, grupo: i.grupo });
  });
  return plano;
}
